
import { createContext, useContext, useState, ReactNode, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { User, UserRole } from "@/types";
import { initializeAdmin, clearUserSession, getUserSession } from "@/services/UserService";
import { useLogin } from "@/hooks/useLogin";
import { useUserManagement } from "@/hooks/useUserManagement";

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  isLoading: boolean;
  isAdmin: () => boolean;
  isParceiro: () => boolean;
  canAccessResource: (resourceCreatorId?: string) => boolean;
  canAccessCalculator: () => boolean;
  hasPermission: (permission: string) => boolean;
  register: (nome: string, email: string, password: string, role: UserRole, permissions?: Record<string, boolean>) => Promise<boolean>;
  updateUser: (userId: string, data: Partial<User>) => Promise<boolean>;
  resetPassword: (email: string) => Promise<boolean>;
  changePassword: (userId: string, newPassword: string) => Promise<boolean>;
  getAllUsers: () => User[];
  deleteUser: (userId: string) => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth deve ser usado dentro de um AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  // Hooks para funcionalidades de auth
  const { login, isLoading: loginLoading } = useLogin(setUser);
  const { 
    register, 
    updateUser, 
    resetPassword, 
    changePassword, 
    deleteUser, 
    getAllUsers 
  } = useUserManagement(user, setUser);

  // Inicializar o administrador padrão se não existir
  useEffect(() => {
    initializeAdmin();
    setIsLoading(false);
  }, []);

  // Verificar se o usuário já está logado ao carregar a página
  useEffect(() => {
    const storedUser = getUserSession();
    if (storedUser) {
      setUser(storedUser);
    }
    setIsLoading(false);
  }, []);

  const logout = () => {
    setUser(null);
    clearUserSession();
    toast.info("Logout realizado com sucesso");
    navigate("/login");
  };

  const isAdmin = () => {
    return user?.role === UserRole.ADMIN;
  };
  
  const isParceiro = () => {
    return user?.role === UserRole.PARCEIRO;
  };
  
  // Verifica se o usuário tem uma permissão específica
  const hasPermission = (permission: string) => {
    if (!user) return false;
    
    // Administradores têm todas as permissões
    if (user.role === UserRole.ADMIN) return true;
    
    // Verifica a permissão específica
    return !!user.permissions?.[permission];
  };
  
  // Verifica se o usuário pode acessar a calculadora solar
  const canAccessCalculator = () => {
    return hasPermission('canAccessCalculator');
  };

  // Verifica se o usuário pode acessar um recurso
  // Para administradores: acesso irrestrito
  // Para vendedores e parceiros: acesso apenas a recursos criados por eles
  const canAccessResource = (resourceCreatorId?: string) => {
    if (!user) return false;
    
    // Administradores têm acesso irrestrito
    if (user.role === UserRole.ADMIN) return true;
    
    // Se não tem criador especificado, não podemos verificar
    if (!resourceCreatorId) return false;
    
    // Vendedores e parceiros só podem acessar seus próprios recursos
    return resourceCreatorId === user.id;
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      login, 
      logout, 
      isLoading: isLoading || loginLoading, 
      isAdmin,
      isParceiro,
      canAccessResource,
      canAccessCalculator,
      hasPermission,
      register,
      updateUser,
      resetPassword,
      changePassword,
      getAllUsers,
      deleteUser
    }}>
      {children}
    </AuthContext.Provider>
  );
};
